/**
 * MetricDetail (/metric/:id) — the full detail surface for one metric: trust chips,
 * the trend with its envelope (reused TrendChart over the page's single /resolve, no
 * refetch — CD-7), the definition, and a "walk this point down → drill" CTA that opens
 * the reconciling drill in place (shared DrillSection). Honest "no detail grain" for
 * Sage-served metrics. Scope (site/cadence/window/drill) rides the URL via useScope.
 */

import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import InfoBanner from '../components/InfoBanner'
import DrillSection from '../components/DrillSection'
import TrendChart from '../components/kit/TrendChart'
import TrustChip from '../components/kit/TrustChip'
import { buildTrendSeries } from '../components/kit/trendSeries'
import { useMetrics } from '../hooks/useMetrics'
import { useResolve } from '../hooks/useResolve'
import { useScope, type Scope } from '../hooks/useScope'
import type { CatalogueResponse, RegistryMetric, ResolveResponse, SignOff } from '../types/metrics'

const SIGN_OFF_TONE: Record<SignOff, 'good' | 'info' | 'warn'> = { draft: 'warn', proposed: 'info', signed: 'good' }
const CARD = 'border border-line rounded-xl bg-panel overflow-hidden'
const HEAD = 'flex items-center gap-2.5 px-4 py-3 border-b border-line2 font-semibold text-[13.5px] text-ink'

function findMetric(catalogue: CatalogueResponse, id: string): RegistryMetric | null {
  for (const g of catalogue.pillars) {
    const m = g.metrics.find((x) => x.id === id)
    if (m) return m
  }
  return null
}

function Chips({ metric }: { metric: RegistryMetric }) {
  const kw = metric.dq_flags.some((f) => f.toUpperCase().includes('KNOWN-WRONG'))
  return (
    <div className="flex gap-1.5 flex-wrap">
      <TrustChip tone="neutral" label={metric.direction === 'higher_is_better' ? '↑ higher better' : '↓ lower better'} />
      <TrustChip tone={SIGN_OFF_TONE[metric.sign_off]} label={`sign-off · ${metric.sign_off}`} />
      {kw && <TrustChip tone="bad" label="KNOWN-WRONG" />}
      {metric.dq_flags.length > 0 && !kw && <TrustChip tone="warn" label="DQ" title={metric.dq_flags.join(' · ')} />}
    </div>
  )
}

function Definition({ metric }: { metric: RegistryMetric }) {
  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <div className="font-mono text-[9.5px] tracking-[.1em] uppercase text-mut2 mb-1">{label}</div>
      <div className="text-[13px] text-ink">{children}</div>
    </div>
  )
  return (
    <div className={CARD}>
      <div className={HEAD}>Definition</div>
      <div className="px-4 py-3.5 flex flex-col gap-3">
        <Field label="Formula">{metric.formula}</Field>
        <div className="flex gap-8">
          <Field label="Owner">{metric.owner}</Field>
          <Field label="Pillar">{metric.pillar}</Field>
        </div>
      </div>
    </div>
  )
}

function TrendCard({ metricId, scope, resolve, title }: { metricId: string; scope: Scope; resolve: ResolveResponse; title: string }) {
  const series = buildTrendSeries(resolve, metricId, scope.site, title)
  return (
    <div className={CARD}>
      <div className={HEAD}>
        Trust envelope on the line
        <span className="font-mono text-[10px] text-mut2 font-normal">RAG dots · target · locked band · null gaps</span>
      </div>
      <div className="px-2 py-3">
        {series.servedCount === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-mut2">No {title} values served for {scope.site} over this window.</p>
        ) : (
          <TrendChart series={series} />
        )}
      </div>
    </div>
  )
}

function DrillCta({ metric, scope }: { metric: RegistryMetric; scope: Scope }) {
  if (!metric.drill?.enabled || scope.cadence !== 'monthly') {
    return (
      <div className="px-4 py-5 text-center text-[13px] text-mut">
        <span className="font-semibold text-ink block mb-1">No detail grain</span>
        Served from the ledger as a figure with no transaction grain to walk down to — a designed refusal, not an error.
      </div>
    )
  }
  return (
    <div className="px-4 py-4">
      <button
        onClick={() => scope.openDrill(metric.id, scope.periodTo)}
        className="cx-btn px-5 py-2.5 rounded-lg border border-brand bg-info-bg text-brand text-[13px] font-semibold"
      >
        Walk {scope.periodTo} down → drill
      </button>
    </div>
  )
}

export default function MetricDetail(): React.JSX.Element {
  const { metricId = '' } = useParams()
  const scope = useScope()
  const catalogue = useMetrics()
  const resolve = useResolve(scope.periodFrom, scope.periodTo, scope.cadence, 'all')

  const error = catalogue.error ?? resolve.error
  if (error) return <div className="cx-page py-6"><InfoBanner variant="error" icon={AlertCircle} title="Metric unavailable" message={error.message} /></div>
  if (!catalogue.data || !resolve.data) return <div className="cx-page py-12 text-center text-sm text-mut2">Loading metric…</div>

  const metric = findMetric(catalogue.data, metricId)
  if (!metric) return <div className="cx-page py-6"><InfoBanner variant="warning" message={`No metric '${metricId}' in the registry.`} /></div>

  return (
    <div className="cx-fade cx-page py-6 pb-20 space-y-4">
      <Link to="/" className="cx-btn inline-block px-3 py-1.5 rounded-lg border border-line bg-panel text-mut text-xs">← Matrix</Link>
      <div className="flex justify-between items-start gap-5 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[.16em] uppercase text-brand mb-1.5">Metric detail · trend + drill</div>
          <h1 className="text-[26px] font-bold tracking-[-.02em] text-ink">{metric.title}</h1>
        </div>
        <Chips metric={metric} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4">
        <TrendCard metricId={metricId} scope={scope} resolve={resolve.data} title={metric.title} />
        <Definition metric={metric} />
      </div>
      <div className={CARD}>
        <div className={HEAD}>Selected point · {scope.periodTo} — drill</div>
        {scope.drill ? <div className="p-4"><DrillSection scope={scope} catalogue={catalogue.data} /></div> : <DrillCta metric={metric} scope={scope} />}
      </div>
    </div>
  )
}
